import React, { useEffect, useRef, useState } from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useDispatch, useSelector } from 'react-redux';

// Material UI
import Dialog from '@material-ui/core/Dialog';

// app: components
import Backdrop from 'components/Backdrop';
import ErrorBoundary from 'components/ErrorBoundary';

// app: constants
import { MODALS } from 'constants/view';

// app: hooks
import useFeatureToggle from 'hooks/useFeatureToggle';
import { usePremiumPromoPending } from 'hooks/useInterruptions';

// app: services
import { canShowPrompt, markPromptShown, unmarkPromptShown } from 'services/onboardingGuard';
import {
  ONBOARDING,
  getSnapshot,
  reportOnboarding,
  reopenOnboarding,
  reportReader,
  withdrawOnboarding,
} from 'services/interruptions';
import { hasPanelRecord, parsePanels, serializePanels } from 'services/onboardingPanels';
import {
  hasChosenPreferences,
  isNewUser,
  isSuppressedPath,
  shouldPromptPreferences,
} from 'services/onboardingPreferences';

import saveUserSettings from 'actions/user/saveUserSettings';

import PreferencesFlow, {
  recordPreferencesOutcome,
  recordPreferencesShown,
} from './PreferencesFlow';

// assets
import CssPreferencesOnboarding from './PreferencesOnboarding.scss';

const asArray = value => (Array.isArray(value) ? value : []);

// Wait this long for the route to settle before opening, so a redirect is never interrupted.
const OPEN_DELAY_MS = 800;

// The remote off switch (a feature toggle). The prompt only runs while it is explicitly on: if it
// is off, missing, or cannot be read, the prompt does not show.
export const PREFERENCES_PROMPT_TOGGLE = 'onboarding_preferences_prompt';

/**
 * Opens the preferences questions wherever the reader happens to be:
 * - as a lightbox for a later re-ask or the `?onboarding=preferences` deep link;
 * - as a full page for a brand-new user the Premium offer did not hand over to the preferences
 *   page (corporate and voucher sign-ups skip the Premium offer).
 *
 * It never opens over the Premium offer, the sign-up pages or the preferences page itself, and it
 * never shows the Premium offer. It is written defensively, because a prompt that keeps coming
 * back would make the app unusable:
 * - it is recorded on this device *before* it opens (see services/onboardingGuard), so a failed
 *   save to the server cannot make it return, and it needs that record to be readable;
 * - at most once a session and once a day, and three times in all, on a device;
 * - any error while deciding or showing it means "do not show".
 */
function PreferencesOnboarding() {
  useStyles(CssPreferencesOnboarding);
  const dispatch = useDispatch();
  const userId = useSelector(state => state.user.id);
  const loggedIn = useSelector(state => state.user.logged_in);
  const registeredAt = useSelector(state => state.user.registered_at);
  const settings = useSelector(state => state.user.settings);
  const settingsResolved = useSelector(state => state.user.settingsResolved || false);
  const pathname = useSelector(state => state.router.pathname);
  const routerLoaded = useSelector(state => state.router.state === 'loaded');
  const paymentModalOpen = useSelector(
    state => state.modals?.openModalName === MODALS.IN_APP_PAYMENT,
  );
  const premiumPromoPending = usePremiumPromoPending();
  const [toggleEnabled, toggleLoading] = useFeatureToggle(PREFERENCES_PROMPT_TOGGLE);
  const switchedOn = toggleEnabled === true;

  const [open, setOpen] = useState(false);
  const [finished, setFinished] = useState(false);
  const [asPage, setAsPage] = useState(false);

  // A different reader (the apps log out and in without reloading) is a fresh start: what happened
  // for the last one must not stop the new one from being decided about.
  useEffect(
    () => {
      setFinished(false);
    },
    [userId, loggedIn],
  );

  // The ask history, and whether anything was recorded or chosen, are only ever added to here. If
  // they are wound back (the test links rewind or reset them), what was decided in this visit no
  // longer holds: it may be due again, so it is allowed to open again, however it was settled.
  const askedAt = asArray(settings?.onboarding_preferences_asked_at);
  const askedKey = JSON.stringify(askedAt);
  const marked =
    !!settings?.onboarding_preferences_status ||
    hasChosenPreferences(settings) ||
    hasPanelRecord(parsePanels(settings));
  const previous = useRef({ askedKey, marked });
  useEffect(
    () => {
      const before = JSON.parse(previous.current.askedKey);
      const wasMarked = previous.current.marked;
      previous.current = { askedKey, marked };
      const extended =
        before.length <= askedAt.length && before.every((time, i) => time === askedAt[i]);
      if (!extended || (wasMarked && !marked)) {
        setFinished(false);
        reopenOnboarding(loggedIn);
      }
    },
    [askedKey, marked], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // The latest settings, for the callbacks below, without re-running their effects.
  const asksBeforeOpening = useRef([]);
  const panelsBeforeOpening = useRef(serializePanels(parsePanels({})));
  // Tells the questions when they are withdrawn before they could be seen, so nothing is recorded.
  const flowControl = useRef({ discarded: false });
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  // The Premium offer (which the apps show right after signing in) comes first.
  const premiumInTheWay = paymentModalOpen || premiumPromoPending;
  const blocked =
    !switchedOn || !routerLoaded || premiumInTheWay || isSuppressedPath(pathname) || !loggedIn;

  // Whatever was decided while the Premium offer was in front (that there would be no onboarding,
  // say, because the settings or the remote switch had not arrived yet) was decided without the
  // reader having got past the offer. The offer is separate from the onboarding, and however it is
  // dismissed (the close box or "Skip for now") the onboarding is decided afresh when it is gone.
  const premiumWasInTheWay = useRef(false);
  useEffect(
    () => {
      if (premiumWasInTheWay.current && !premiumInTheWay) {
        // Only a "will not show" is reopened: one that was shown and closed stays closed.
        const { onboarding } = getSnapshot();
        if (onboarding === ONBOARDING.NONE || onboarding === ONBOARDING.UNKNOWN) {
          reopenOnboarding(loggedIn);
        }
      }
      premiumWasInTheWay.current = premiumInTheWay;
    },
    [premiumInTheWay, loggedIn],
  );

  let eligible = false;
  try {
    eligible =
      !blocked &&
      shouldPromptPreferences({
        loggedIn,
        settingsResolved,
        settings,
        pathname,
        paymentModalOpen: premiumInTheWay,
      });
  } catch (error) {
    eligible = false;
  }
  const neverAsked = asArray(settings?.onboarding_preferences_asked_at).length === 0;

  // Whether it is due, leaving out what only holds it back for now (the page being open is a
  // Premium or sign-up page, the route still loading). Used to tell Intercom and the NPS survey that
  // there will be no onboarding in this visit, so they need not wait for it.
  let dueSetAsideTemporaryHolds = false;
  try {
    dueSetAsideTemporaryHolds = shouldPromptPreferences({
      loggedIn,
      settingsResolved,
      settings,
      pathname: '/',
      paymentModalOpen: false,
    });
  } catch (error) {
    dueSetAsideTemporaryHolds = false;
  }
  const willNotShow =
    !loggedIn ||
    (!toggleLoading && !switchedOn) ||
    (settingsResolved && !dueSetAsideTemporaryHolds);

  // Tell the coordinator what the onboarding is doing, so Intercom starts after it closes, and the
  // NPS survey is skipped when it was shown (see services/interruptions).
  useEffect(
    () => {
      reportReader(loggedIn);
    },
    [loggedIn],
  );
  const wasOpen = useRef(false);
  const withdrawn = useRef(false);
  const loggedInRef = useRef(loggedIn);
  loggedInRef.current = loggedIn;
  useEffect(
    () => {
      if (open) {
        reportOnboarding(ONBOARDING.SHOWING, loggedIn);
      } else if (wasOpen.current && withdrawn.current) {
        // Pushed aside before it could be seen: it did not happen, and may come back.
        withdrawOnboarding(loggedIn);
      } else if (wasOpen.current) {
        reportOnboarding(ONBOARDING.FINISHED, loggedIn);
      }
      withdrawn.current = false;
      wasOpen.current = open;
    },
    [open, loggedIn],
  );
  // If it is taken away while open (its layout unmounts, or it errors), nobody else will say it has
  // closed, and Intercom would wait for ever.
  useEffect(
    () => () => {
      if (wasOpen.current) {
        reportOnboarding(ONBOARDING.FINISHED, loggedInRef.current);
      }
    },
    [],
  );
  useEffect(
    () => {
      if (!open && willNotShow) {
        reportOnboarding(ONBOARDING.NONE, loggedIn);
      }
    },
    [open, willNotShow, loggedIn],
  );

  // Open once the route has been stable for a moment.
  useEffect(
    () => {
      if (!eligible || open || finished) {
        return undefined;
      }
      const timer = setTimeout(() => {
        try {
          // It already said it will not show in this visit (Intercom and the survey have acted on
          // that), so it keeps its word, even if something such as the remote switch changes later.
          const decided = getSnapshot();
          if (decided.onboarding === ONBOARDING.NONE && decided.forLoggedIn === !!loggedIn) {
            return;
          }
          const now = Date.now();
          if (!canShowPrompt({ userId, now })) {
            reportOnboarding(ONBOARDING.NONE, loggedIn);
            return;
          }
          // Remember it on this device first. If that cannot be done, do not show the prompt.
          if (!markPromptShown({ userId, now })) {
            reportOnboarding(ONBOARDING.NONE, loggedIn);
            return;
          }
          const showAsPage = neverAsked && isNewUser(registeredAt);
          // Counted as an ask now, not when it is closed, so a crash or a closed tab still counts.
          asksBeforeOpening.current = asArray(settingsRef.current?.onboarding_preferences_asked_at);
          panelsBeforeOpening.current = serializePanels(parsePanels(settingsRef.current));
          flowControl.current = { discarded: false };
          recordPreferencesShown(dispatch, settingsRef.current);
          setAsPage(showAsPage);
          // Say so at once, not when React next gets to run its effects: the time limit on holding
          // Intercom must not be able to slip in between and let it start under the onboarding.
          reportOnboarding(ONBOARDING.SHOWING, loggedIn);
          setOpen(true);
        } catch (error) {
          console.warn('The preferences prompt could not be opened', error);
          reportOnboarding(ONBOARDING.NONE, loggedIn);
        }
      }, OPEN_DELAY_MS);
      return () => clearTimeout(timer);
    },
    [eligible, open, finished, neverAsked, registeredAt, userId, dispatch, loggedIn],
  );

  // Choosing something makes the user "ineligible", which must not close the box, so only the
  // blocking conditions close it (including the off switch). Being pushed aside is not an ask.
  useEffect(
    () => {
      if (open && blocked) {
        if (premiumInTheWay) {
          // The Premium offer opened over it, so the reader had no real chance to see it. Give the
          // turn back (on this device and on the server) so it comes again once the offer is done.
          withdrawn.current = true;
          flowControl.current.discarded = true;
          try {
            unmarkPromptShown({ userId });
            dispatch(
              saveUserSettings({
                onboarding_preferences_asked_at: asksBeforeOpening.current,
                onboarding_panels: panelsBeforeOpening.current,
              }),
            );
          } catch (error) {
            console.warn('Could not give the onboarding its turn back', error);
          }
        }
        setOpen(false);
      }
    },
    [open, blocked, premiumInTheWay, userId, dispatch],
  );

  const handleDone = () => {
    setOpen(false);
    setFinished(true);
  };

  // Closed by finishing the questions, or by Esc / clicking outside the lightbox.
  const handleClose = () => {
    try {
      recordPreferencesOutcome(dispatch, settingsRef.current);
    } catch (error) {
      console.warn('The preferences outcome could not be saved', error);
    }
    handleDone();
  };

  if (!open) {
    return null;
  }

  if (asPage) {
    return (
      <div
        className="preferences-onboarding__page"
        role="dialog"
        aria-modal="true"
        aria-labelledby="preferences-onboarding-heading"
      >
        <Backdrop maxWidth="md">
          <PreferencesFlow onDone={handleClose} recordAsk={false} control={flowControl} />
        </Backdrop>
      </div>
    );
  }

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="preferences-onboarding-heading"
      style={{ zIndex: 1400 }}
    >
      <div style={{ padding: '30px 10px' }}>
        <PreferencesFlow onDone={handleClose} recordAsk={false} control={flowControl} />
      </div>
    </Dialog>
  );
}

// A mistake anywhere in the prompt must not take the page down with it: show nothing instead.
function PreferencesOnboardingWithBoundary() {
  return (
    <ErrorBoundary errorMessage={<React.Fragment />}>
      <PreferencesOnboarding />
    </ErrorBoundary>
  );
}

export default PreferencesOnboardingWithBoundary;
